import { GRAY_SCALE_TILE_BASE_PATH, GRAY_SCALE_TILE_COUNT, GrayScaleField } from "./grayScaleField";

/**
 * Rasterises the interpolated field once, so the map can carry a single image
 * instead of one DOM node per interpolated cell — several hundred per map.
 */

/**
 * Pixels per cell in the rasterised field. Comfortably above the size a cell
 * prints at, so the hatching stays clean, while keeping the encoded image small.
 */
const CELL_PIXELS = 24;

function loadImage(source: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`gray scale tile failed to load: ${source}`));
    image.src = source;
  });
}

let tileImages: Promise<HTMLImageElement[]> | null = null;

function loadTileImages(): Promise<HTMLImageElement[]> {
  if (!tileImages) {
    tileImages = Promise.all(
      Array.from({ length: GRAY_SCALE_TILE_COUNT }, (_value, level) =>
        loadImage(GRAY_SCALE_TILE_BASE_PATH.replace("{img}", String(level))),
      ),
    );
    tileImages.catch(() => {
      tileImages = null;
    });
  }
  return tileImages;
}

/**
 * Returns a PNG data URL of the whole field, or null when the field is empty or
 * a tile could not be loaded — in which case the map draws without it rather
 * than failing the sheet.
 */
export async function renderGrayScaleFieldImage(field: GrayScaleField): Promise<string | null> {
  const rowCount = field.length;
  const columnCount = field[0]?.length ?? 0;
  if (!rowCount || !columnCount) return null;

  let tiles: HTMLImageElement[];
  try {
    tiles = await loadTileImages();
  } catch {
    return null;
  }

  const canvas = document.createElement("canvas");
  canvas.width = columnCount * CELL_PIXELS;
  canvas.height = rowCount * CELL_PIXELS;
  const context = canvas.getContext("2d");
  if (!context) return null;

  field.forEach((row, rowIndex) => {
    row.forEach((tileLevel, columnIndex) => {
      const tile = tileLevel == null ? undefined : tiles[tileLevel];
      if (!tile) return;
      context.drawImage(
        tile,
        columnIndex * CELL_PIXELS,
        rowIndex * CELL_PIXELS,
        CELL_PIXELS,
        CELL_PIXELS,
      );
    });
  });

  return canvas.toDataURL("image/png");
}
